/*
 * @Author: liuhongzhuang liuhongzhuang@nextmar.com
 * @Date: 2025-06-04
 * @LastEditors: Please set LastEditors
 * @LastEditTime: 2025-06-12 21:29:58
 * @FilePath: /src/components/Upload/index.tsx
 * @Description: 文件上传组件
 */

import { toast } from '@/components/Toast';
import { awsUpload } from '@/utils/aws-s3';
import logger from '@/utils/client-logger';
import { Box, SxProps } from '@mui/material';
import { DeleteCircleFilled2, FileOutlined, PlusOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import ImagePreviewComponent from './components/ImagePreview';

export type FileType = 'image' | 'file' | 'all';

export type UploadStatus = 'idle' | 'uploading' | 'done' | 'error';

export interface ImageItem {
  id: string;
  url: string;
  s3Url: string;
  status: UploadStatus;
  file?: File;
  type?: string;
}

export interface UploadProps {
  /** 文件上传场景ID (2:处方图, 4:商品评论, 5:Email附件) */
  sceneId: number;
  /** 上传文件类型 */
  fileType?: FileType;
  /** 接受上传的文件类型 */
  accept?: string;
  /** 多文件上传 */
  multiple?: boolean;
  /** 文件大小限制（MB） */
  maxSize?: number;
  /** 是否禁用上传 */
  disabled?: boolean;
  /** 支持的图片格式 */
  imgAccept?: string;
  /** 是否需要展示图片 */
  showImage?: boolean;
  /** 是否显示上传完成图片的列表 */
  showFileList?: boolean;
  /** 自定义样式 */
  className?: string;
  /** 是否需要拍照 camera 用于拍照 */
  capture?: string;
  /** 文件上传成功回调 */
  onChange?: (fileUrl: string | string[]) => void;
  /** 文件上传成功回调 */
  onChangeInfo?: (fileInfo: Array<{ type: string; s3Url: string }>) => void;
  /** 自定义内容 */
  children?: ReactNode;
  /** 最大上传数量 */
  maxCount?: number;
  /** 是否支持拖拽上传 */
  enableDrag?: boolean;
  /** 自定义样式 */
  sx?: SxProps;
  /** 上传成功或失败是否toast */
  toastConfig?: { success: boolean; fail: boolean };
  /** 上传开始回调 */
  onStartUpload?: () => void;
  /** 点击上传回调 */
  onClickUpload?: () => void;
  /** 上传状态变化回调 */
  onUploadChange?: (isUploading: boolean) => void;
}

const Upload = ({
  sceneId,
  fileType = 'image',
  accept,
  multiple = false,
  maxSize = 10,
  disabled = false,
  imgAccept = 'image/jpeg,image/png,image/jpg,.jpg,.jpeg,.png',
  showImage = false,
  showFileList = false,
  capture,
  className,
  onChange,
  onChangeInfo,
  children,
  maxCount,
  sx,
  enableDrag = true,
  toastConfig = { success: true, fail: true },
  onStartUpload,
  onClickUpload,
  onUploadChange,
}: UploadProps) => {
  const t = useTranslations('components.upload');
  const [imageList, setImageList] = useState<ImageItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const imageListRef = useRef<ImageItem[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 获取接受的文件类型
  const getAccept = useCallback(() => {
    if (accept) return accept;
    return imgAccept;
  }, [accept, imgAccept]);

  // 验证文件类型和大小
  const validateFile = useCallback(
    (file: File) => {
      const acceptParam = getAccept();
      let isValidType = false;

      // 处理accept参数
      if (acceptParam) {
        const acceptTypes = acceptParam.split(',').map((type) => type.trim());

        // 检查是否为扩展名格式 (.jpg, .jpeg, .png)
        const extensionTypes = acceptTypes.filter((type) => type.startsWith('.'));
        const mimeTypes = acceptTypes.filter((type) => !type.startsWith('.'));

        if (extensionTypes.length > 0) {
          // 扩展名检查
          const fileName = file.name.toLowerCase();
          isValidType = extensionTypes.some((ext) => fileName.endsWith(ext.toLowerCase()));
        }

        if (!isValidType && mimeTypes.length > 0) {
          // MIME类型检查
          isValidType = mimeTypes.some((type) => {
            // 支持通配符 image/* 格式
            if (type.endsWith('/*')) {
              const mainType = type.split('/')[0];
              return file.type.startsWith(`${mainType}/`);
            }
            return file.type === type;
          });
        }
      } else {
        // 如果没有指定accept，则接受所有文件
        isValidType = true;
      }

      if (!isValidType) {
        if (toastConfig.fail) {
          toast.error(t('formatError', { formats: imgAccept }));
        }
        return false;
      }

      const fileSizeInMB = file.size / (1024 * 1024);
      if (fileSizeInMB > maxSize) {
        if (toastConfig.fail) {
          toast.error(t('fileSizeError', { size: maxSize }));
        }
        return false;
      }

      return true;
    },
    [getAccept, maxSize, imgAccept, t],
  );

  // 处理单个文件上传
  const handleSingleFileUpload = useCallback(
    async (file: File) => {
      if (!validateFile(file)) return null;

      // 创建唯一ID
      const fileId = `temp_${Date.now()}_${file.name}`;
      const localUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : '';

      setImageList((prev) => [
        ...prev,
        {
          id: fileId,
          url: localUrl,
          s3Url: '',
          status: 'uploading',
          type: file.type,
          file,
        },
      ]);

      try {
        const res = await awsUpload(sceneId, file, () => {
          // 简化逻辑，不再处理进度
        });

        if (toastConfig.success) {
          toast.success(t('uploadSuccess'));
        }

        // 更新状态为done
        setImageList((prev) =>
          prev.map((item) =>
            item.id === fileId
              ? {
                  ...item,
                  s3Url: res as string,
                  status: 'done',
                  type: file.type,
                }
              : item,
          ),
        );

        return { id: fileId, s3Url: res as string, type: file.type };
      } catch (error) {
        if (toastConfig.fail) {
          toast.error(t('uploadFail'));
        }
        logger.error(error);
        // 更新状态为error
        setImageList((prev) =>
          prev.map((item) =>
            item.id === fileId
              ? {
                  ...item,
                  status: 'error',
                }
              : item,
          ),
        );
        return null;
      }
    },
    [validateFile, sceneId, t, toastConfig],
  );

  // 处理文件上传（单个或多个）
  const handleUpload = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      // 检查是否超过最大上传数量限制
      if (maxCount !== undefined) {
        const currentCount = imageList.length;
        const remainingCount = maxCount - currentCount;

        if (remainingCount <= 0) {
          if (toastConfig.fail) {
            toast.error(t('maxCountError', { count: maxCount }));
          }
          return;
        }

        // 如果还可以上传，但数量有限制，裁剪文件数组
        if (files.length > remainingCount) {
          // 只取允许上传的最大数量
          const filesToUpload = files.slice(0, remainingCount);

          files = filesToUpload;

          toast.error(t('maxCountError', { count: maxCount }));
        }
      }

      try {
        const valid = files.every((file) => validateFile(file));
        if (!valid) {
          return;
        }
        setIsUploading(true);
        onStartUpload?.();

        if (!multiple) {
          // 单文件上传 - 清除之前的文件
          setImageList([]);
          const result = await handleSingleFileUpload(files[0]);
          if (result) {
            // 上传成功
            onChange?.(result.s3Url);
            onChangeInfo?.([{ type: result.type, s3Url: result.s3Url }]);
          }
        } else {
          // 多文件上传
          const results = await Promise.all(files.map(handleSingleFileUpload));
          const successfulUploads = results.filter(Boolean) as Array<{
            id: string;
            s3Url: string;
            type: string;
          }>;
          if (successfulUploads.length > 0) {
            // 先筛选出已完成的图片，避免重复筛选
            const doneImages = imageList.filter((item) => item.status === 'done');

            // 合并所有成功的图片信息
            const allImagesInfo = [
              // imageList中已完成的图片
              ...doneImages.map((item) => ({
                type: item.file?.type,
                s3Url: item.s3Url,
              })),
              // successfulUploads中的图片
              ...successfulUploads.map((item) => ({
                type: item.type,
                s3Url: item.s3Url,
              })),
            ].filter((item) => item.s3Url); // 过滤掉没有s3Url的项

            // 提取所有有效的URL
            const allUrls = allImagesInfo.map((item) => item.s3Url);

            onChange?.(allUrls);
            onChangeInfo?.(allImagesInfo);
          }
        }
      } finally {
        setIsUploading(false);
      }
    },
    [multiple, handleSingleFileUpload, imageList, onChange, maxCount, t, toastConfig],
  );

  // 通用拖拽事件处理
  const handleDragEvent = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (disabled || isUploading || !enableDrag) return;
      e.preventDefault();
      e.stopPropagation();
    },
    [disabled, isUploading, enableDrag],
  );

  /* 重试上传功能暂时注释
  const handleRetryUpload = useCallback(
    async (id: string) => {
      // 查找失败的文件
      const failedItem = imageList.find((item) => item.id === id && item.status === 'error');
      if (!failedItem || !failedItem.file) return;

      // 更新状态为上传中
      setImageList((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                status: 'uploading',
              }
            : item,
        ),
      );

      try {
        // 重新上传文件
        const res = await awsUpload(sceneId, failedItem.file, () => {});

        if (toastConfig.success) {
          toast.success(t('uploadSuccess'));
        }

        // 更新状态为done
        setImageList((prev) =>
          prev.map((item) =>
            item.id === id
              ? {
                  ...item,
                  s3Url: res as string,
                  status: 'done',
                }
              : item,
          ),
        );

        // 如果是单文件上传模式
        if (!multiple) {
          onChange?.(res as string);
          onChangeInfo?.([{ type: failedItem.type || '', s3Url: res as string }]);
        } else {
          // 多文件上传模式
          const doneImages = imageList.filter((item) => item.status === 'done' || item.id === id);
          const allUrls = doneImages.map((item) => (item.id === id ? (res as string) : item.s3Url));
          const allImagesInfo = doneImages.map((item) => ({
            type: item.type || '',
            s3Url: item.id === id ? (res as string) : item.s3Url,
          }));

          onChange?.(allUrls);
          onChangeInfo?.(allImagesInfo);
        }
      } catch (error) {
        // 重试也失败
        if (toastConfig.fail) {
          toast.error(t('uploadFail'));
        }
        logger.error(error);

        // 更新状态为error
        setImageList((prev) =>
          prev.map((item) =>
            item.id === id
              ? {
                  ...item,
                  status: 'error',
                }
              : item,
          ),
        );
      }
    },
    [imageList, multiple, onChange, onChangeInfo, sceneId, t, toastConfig],
  );
  */

  // 处理文件放置
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (disabled || isUploading || !enableDrag) return;

      e.preventDefault();
      e.stopPropagation();

      const { files } = e.dataTransfer;
      if (files?.length) {
        const filesArray = Array.from(files);
        handleUpload(multiple ? filesArray : [filesArray[0]]);
      }
    },
    [multiple, handleUpload, enableDrag, disabled, isUploading],
  );

  // 处理input的change事件
  const handleFileInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files;
      if (fileList?.length) {
        await handleUpload(Array.from(fileList));
      }
    },
    [handleUpload],
  );

  // 点击上传
  const handleClick = useCallback(() => {
    if (disabled || isUploading) return;
    if (!fileInputRef.current) return;

    const input = fileInputRef.current;

    // 更新input属性
    input.accept = getAccept();
    input.multiple = multiple && (!maxCount || maxCount > 1);
    if (capture) {
      input.setAttribute('capture', capture);
    } else {
      input.removeAttribute('capture');
    }

    // 清空之前的值，确保可以重复选择同一个文件
    input.value = '';
    input.click();

    onClickUpload?.();
  }, [disabled, isUploading, getAccept, multiple, maxCount, capture, onClickUpload]);

  // 删除图片
  const handleDeleteImg = useCallback(
    (id: string) => {
      // 先找到要删除的图片，如果有本地URL需要释放
      const itemToDelete = imageList.find((item) => item.id === id);
      if (itemToDelete?.url && itemToDelete.url.startsWith('blob:')) {
        URL.revokeObjectURL(itemToDelete.url);
      }

      // 更新图片列表
      const updatedList = imageList.filter((item) => item.id !== id);
      setImageList(updatedList);

      // 触发onChange回调，传递最新的文件列表
      const doneItems = updatedList.filter((item) => item.status === 'done');
      if (multiple) {
        // 确保所有URL有效
        const validUrls = doneItems
          .filter((item) => item.s3Url) // 只使用有效的s3Url
          .map((item) => item.s3Url);
        onChange?.(validUrls);

        const validImageInfo = doneItems
          .filter((item) => item.s3Url) // 只使用有效的s3Url
          .map((item) => ({ type: item.type || '', s3Url: item.s3Url }));
        onChangeInfo?.(validImageInfo);
      } else if (doneItems.length > 0 && doneItems[0].s3Url) {
        onChange?.(doneItems[0].s3Url);
        onChangeInfo?.([{ type: doneItems[0].type || '', s3Url: doneItems[0].s3Url }]);
      } else {
        onChange?.(multiple ? [] : '');
        onChangeInfo?.(multiple ? [] : undefined);
      }
    },
    [imageList, multiple, onChange, onChangeInfo],
  );

  // 用于cleanup
  useEffect(() => {
    imageListRef.current = imageList;
  }, [imageList]);

  // 组件卸载时清理URL对象
  useEffect(() => {
    return () => {
      // 清理函数，防止内存泄漏
      imageListRef.current.forEach((item) => {
        if (item.url && item.url.startsWith('blob:')) {
          URL.revokeObjectURL(item.url);
        }
      });
    };
  }, []);

  useEffect(() => {
    onUploadChange?.(isUploading);
  }, [onUploadChange, isUploading]);

  // 检查是否可以继续添加图片
  const canAddMore = maxCount === undefined || imageList.length < maxCount;

  return (
    <>
      <Box
        className={className}
        sx={{
          ...sx,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mt: 2,
        }}
      >
        {showImage && (
          <ImagePreviewComponent
            fileType={fileType}
            fileUrls={imageList}
            onDelete={handleDeleteImg}
            mode="upload"
          />
        )}
        {canAddMore && (
          <>
            {/* 复用或创建input元素以提高iOS Safari兼容性 */}
            <input
              ref={fileInputRef}
              className="hidden"
              type="file"
              onChange={handleFileInputChange}
            />
            <Box
              onClick={canAddMore ? handleClick : undefined}
              onDragEnter={enableDrag ? (e) => handleDragEvent(e) : undefined}
              onDragOver={enableDrag ? (e) => handleDragEvent(e) : undefined}
              onDragLeave={enableDrag ? (e) => handleDragEvent(e) : undefined}
              onDrop={enableDrag ? handleDrop : undefined}
              sx={{
                cursor: canAddMore && !disabled ? 'pointer' : 'default',
              }}
            >
              {children || (
                <div className="w-[78px] h-[78px] bg-auxiliary-light-gray rounded-8 flex items-center justify-center">
                  <PlusOutlined />
                </div>
              )}
            </Box>
          </>
        )}
      </Box>
      {showFileList && (
        <div className="uploaded-file-list text-[14px] mt-12">
          {imageList
            .filter((item) => item.status === 'done')
            .map((item) => (
              <div
                key={item.id}
                className="uploaded-file-item group py-5 px-8 mb-8 relative bg-[#f4f4f4] rounded-6"
              >
                <span className="uploaded-file-icon mr-[10px]">
                  <FileOutlined className="inline-flex" />
                </span>
                <span className="uploaded-file-name">{item.file?.name}</span>
                <span
                  className="uploaded-file-status absolute right-[11px] text-[20px] cursor-pointer"
                  onClick={() => handleDeleteImg(item.id)}
                >
                  <DeleteCircleFilled2 />
                </span>
              </div>
            ))}
        </div>
      )}
    </>
  );
};

export default Upload;
